import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { communitiesApi, memoriesApi, Community, AgentMemory } from '@/services/api/agentApi';
import { ensureOptionalAgentServiceAvailable } from '@/services/api/agentServiceAvailability';
import * as errorUtils from '@/utils/errors';

interface CommunitiesStoreState {
  communities: Community[];
  systemCommunities: Community[];
  userCommunity: Community | null;
  isLoading: boolean;
  error: string | null;
  // Community detail + knowledge
  selectedCommunity: Community | null;
  communityMemories: AgentMemory[];
  memoriesLoading: boolean;
}

interface CommunitiesStoreActions {
  fetchCommunities: (params?: Record<string, string>) => Promise<void>;
  fetchSystemCommunities: () => Promise<void>;
  fetchUserCommunity: () => Promise<void>;
  createSystemCommunity: (name: string) => Promise<void>;
  // Community detail + knowledge
  selectCommunity: (community: Community | null) => void;
  fetchCommunityMemories: (communityId: string) => Promise<void>;
  createCommunityMemory: (data: Partial<AgentMemory>) => Promise<void>;
  deleteCommunityMemory: (memoryId: string, communityId: string) => Promise<void>;
  updateCommunityMemory: (memoryId: string, data: Partial<AgentMemory>) => Promise<void>;
}

export const useCommunitiesStore = create<CommunitiesStoreState & CommunitiesStoreActions>()(
  immer((set, get) => ({
    communities: [],
    systemCommunities: [],
    userCommunity: null,
    isLoading: false,
    error: null,
    selectedCommunity: null,
    communityMemories: [],
    memoriesLoading: false,

    fetchCommunities: async (params) => {
      set((s) => { s.isLoading = true; });
      try {
        if (!(await ensureOptionalAgentServiceAvailable())) {
          set((s) => { s.communities = []; s.isLoading = false; });
          return;
        }

        const result = await communitiesApi.list(params);
        set((s) => { s.communities = result.data; s.isLoading = false; });
      } catch (error: unknown) {
        set((s) => { s.error = errorUtils.getErrorMessage(error); s.isLoading = false; });
      }
    },

    fetchSystemCommunities: async () => {
      set((s) => { s.isLoading = true; });
      try {
        if (!(await ensureOptionalAgentServiceAvailable())) {
          set((s) => { s.systemCommunities = []; s.isLoading = false; });
          return;
        }

        const result = await communitiesApi.getSystem();
        set((s) => { s.systemCommunities = result.data; s.isLoading = false; });
      } catch (error: unknown) {
        set((s) => { s.error = errorUtils.getErrorMessage(error); s.isLoading = false; });
      }
    },

    fetchUserCommunity: async () => {
      try {
        if (!(await ensureOptionalAgentServiceAvailable())) {
          set((s) => { s.userCommunity = null; });
          return;
        }

        const result = await communitiesApi.getMine();
        set((s) => { s.userCommunity = result.data; });
      } catch (error: unknown) {
        set((s) => { s.error = errorUtils.getErrorMessage(error); });
      }
    },

    createSystemCommunity: async (name) => {
      await communitiesApi.create({ name, type: 'system' });
      await get().fetchSystemCommunities();
    },

    selectCommunity: (community) => {
      set((s) => {
        s.selectedCommunity = community;
        s.communityMemories = [];
        s.memoriesLoading = false;
      });
    },

    fetchCommunityMemories: async (communityId) => {
      set((s) => { s.memoriesLoading = true; });
      try {
        if (!(await ensureOptionalAgentServiceAvailable())) {
          set((s) => { s.communityMemories = []; s.memoriesLoading = false; });
          return;
        }

        const result = await memoriesApi.list({ community_id: communityId });
        set((s) => { s.communityMemories = result.data; s.memoriesLoading = false; });
      } catch {
        set((s) => { s.memoriesLoading = false; });
      }
    },

    createCommunityMemory: async (data) => {
      await memoriesApi.create(data);
      const community = get().selectedCommunity;
      if (community) {
        await get().fetchCommunityMemories(community.community_id);
      }
    },

    deleteCommunityMemory: async (memoryId, communityId) => {
      await memoriesApi.delete(memoryId);
      await get().fetchCommunityMemories(communityId);
    },

    updateCommunityMemory: async (memoryId, data) => {
      await memoriesApi.update(memoryId, data);
      const community = get().selectedCommunity;
      if (community) {
        await get().fetchCommunityMemories(community.community_id);
      }
    },
  }))
);

// Reset on logout to prevent data leakage between sessions
if (typeof window !== 'undefined') {
  import('../auth/auth-store').then(({ useAuthStore }) => {
    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, prev) => {
        if (prev && !isAuthenticated) {
          useCommunitiesStore.setState({
            communities: [], systemCommunities: [], userCommunity: null,
            isLoading: false, error: null, selectedCommunity: null,
            communityMemories: [], memoriesLoading: false,
          });
        }
      }
    );
  });
}
